import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Typography
} from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import { useHistory, useLocation } from 'react-router';
import { connect } from 'react-redux';
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';
import { ApplicationState, AppActionCreators, AppState } from '../../store';
import { AuthApiService } from '../../services/AuthApiService';
import { LoginPayloadType } from '../../interfaces/Auth';
import { ApplicationCodeEnum } from '../../helpers/Constants';
import { ApplicationContext, AppContext } from '../../context/Contexts';
import Logo from '../_shared/Logo';
import LanguageSelector from './LanguageSelector';
import { captureAuthError } from '../../helpers/Sentry';
import { getErrorMessageKey } from '../../helpers/RequestErrors';

type ResetPasswordProps = AppState & typeof AppActionCreators;

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const ResetPassword: React.FC<ResetPasswordProps> = (props) => {
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const query = useQuery();
  const context = useContext(ApplicationContext);
  const translatorService = (context as AppContext).translatorService;

  const code = query.get('code');
  const userId = query.get('userId');
  const email = query.get('email');
  const isConfigMode = query.get('isConfig') === 'true';

  const encodedCode = encodeURIComponent(code || '');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const t = useCallback(
    (key: string, fallback: string) => translatorService.Tranlate(key, fallback),
    [translatorService]
  );

  useEffect(() => {
    ValidatorForm.addValidationRule('isPasswordMatch', (value: string) => {
      return value === newPassword;
    });
    ValidatorForm.addValidationRule('hasLowercase', (value: string) => /[a-z]/.test(value));
    ValidatorForm.addValidationRule('hasUppercase', (value: string) => /[A-Z]/.test(value));
    ValidatorForm.addValidationRule('hasNumber', (value: string) => /\d/.test(value));
    ValidatorForm.addValidationRule('hasSpecialChar', (value: string) =>
      /[^a-zA-Z0-9]/.test(value)
    );

    return () => {
      ValidatorForm.removeValidationRule('isPasswordMatch');
      ValidatorForm.removeValidationRule('hasLowercase');
      ValidatorForm.removeValidationRule('hasUppercase');
      ValidatorForm.removeValidationRule('hasNumber');
      ValidatorForm.removeValidationRule('hasSpecialChar');
    };
  }, [newPassword]);

  const handleAutoLogin = async (userEmail: string, password: string) => {
    try {
      const loginResponse = await AuthApiService.login({
        email: userEmail,
        password,
        app: ApplicationCodeEnum.CONNECT
      } as LoginPayloadType);

      const loginData = loginResponse.data;

      if (!loginData.success) {
        enqueueSnackbar(
          t('FLEET_AUTO_LOGIN_FAILED', 'Password set successfully. Please log in manually.'),
          { variant: 'info' }
        );
        history.push('/auth/login');
        return;
      }

      if (loginData.twoFactorToken) {
        history.push('/auth/2FA', {
          twoFactorToken: loginData.twoFactorToken,
          email: userEmail
        });
      } else {
        const { access_token, refresh_token } = loginData.tokenResponse;
        props.SetToken(access_token);
        props.SetRefreshToken(refresh_token);
        await props.SetUser(enqueueSnackbar);
        await props.LoadCurrencyRates(props.appUser?.organization);
        history.push('/');
      }
    } catch (err) {
      captureAuthError('Reset Password Auto Login', err);
      enqueueSnackbar(
        t('FLEET_AUTO_LOGIN_FAILED', 'Password set successfully. Please log in manually.'),
        { variant: 'info' }
      );
      history.push('/auth/login');
    }
  };

  const handleSubmit = async () => {
    if (!userId || !code) {
      enqueueSnackbar(t('FLEET_INVALID_LINK', 'Invalid link'), { variant: 'error' });
      history.push('/auth/login');
      return;
    }

    setIsLoading(true);
    try {
      const response = await AuthApiService.resetPassword({
        newPassword,
        userId,
        code: encodedCode,
        isConfig: isConfigMode
      });
      const data = response.data as any;

      if (!data.success) {
        enqueueSnackbar(data.message || t('FLEET_ERROR_MESSAGE', 'An error occurred'), {
          variant: 'error'
        });
        setIsLoading(false);
        return;
      }

      if (email) {
        enqueueSnackbar(t('FLEET_CONFIG_PASS_SUCCESS_MESSAGE', 'Password set successfully'), {
          variant: 'success'
        });
        await handleAutoLogin(email, newPassword);
      } else {
        enqueueSnackbar(
          t(
            'FLEET_RESET_PASS_SUCCESS_MESSAGE',
            'Password changed successfully. You can now log in with your new password'
          ),
          { variant: 'success' }
        );
        history.push('/auth/login');
      }
    } catch (err) {
      captureAuthError('Reset Password', err);
      enqueueSnackbar(t(getErrorMessageKey(err, 'FLEET_ERROR_MESSAGE'), 'An error occurred'), {
        variant: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      position="relative"
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      style={{ backgroundColor: '#f4f5fd' }}
    >
      <LanguageSelector />
      <Box
        style={{
          backgroundColor: '#fff',
          borderRadius: 8,
          padding: '32px',
          maxWidth: 500,
          width: '100%',
          boxShadow: '0 2px 16px rgba(0,0,0,0.08)'
        }}
      >
        <Logo />

        <Typography
          variant="body2"
          style={{ color: '#888', marginBottom: 24, textAlign: 'center' }}
        >
          {isConfigMode
            ? t('FLEET_CONFIG_PASSWORD_SUBTITLE', 'Choose a strong password for your account')
            : t('FLEET_RESET_PASSWORD_SUBTITLE', 'Enter your new password below')}
        </Typography>

        <ValidatorForm onSubmit={handleSubmit} instantValidate={true}>
          <Box mb={2}>
            <TextValidator
              fullWidth
              label={t('FLEET_PASSWORD_TEXT_LABEL', 'Password')}
              variant="outlined"
              type={showPassword ? 'text' : 'password'}
              name="newPassword"
              value={newPassword}
              onChange={(e: any) => setNewPassword(e.target.value)}
              disabled={isLoading}
              autoComplete="new-password"
              validators={[
                'required',
                'minStringLength:8',
                'hasLowercase',
                'hasUppercase',
                'hasNumber',
                'hasSpecialChar'
              ]}
              errorMessages={[
                t('VALIDATORS_REQUIRED', 'This field is required'),
                t('FLEET_PASSWORD_MIN_LENGTH_MESSAGE', 'Min length not fulfilled! (8)'),
                t('FLEET_PASSWORD_LOWERCASE_MESSAGE', 'Must have at least one lowercase letter'),
                t('FLEET_PASSWORD_UPPERCASE_MESSAGE', 'Must have at least one uppercase letter'),
                t('FLEET_PASSWORD_NUMBER', 'At least one number'),
                t('FLEET_PASSWORD_SPECIAL_CHAR_MESSAGE', 'Must have at least one special character')
              ]}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Box>

          <Box mb={3}>
            <TextValidator
              fullWidth
              label={t('FLEET_CONFIRM_PASSWORD_TEXT_LABEL', 'Confirm password')}
              variant="outlined"
              type={showConfirmPassword ? 'text' : 'password'}
              name="confirmPassword"
              value={confirmPassword}
              onChange={(e: any) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
              autoComplete="new-password"
              validators={['required', 'isPasswordMatch']}
              errorMessages={[
                t('FLEET_CONFIRM_PASSWORD_REQUIRED', 'Please confirm your password'),
                t('FLEET_PASSWORDS_MUST_MATCH_MESSAGE', 'Passwords must match')
              ]}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      edge="end"
                      size="small"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Box>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={isLoading}
            style={{ textTransform: 'none', marginBottom: 12 }}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : isConfigMode ? (
              t('FLEET_CONFIG_PASSWORD_BUTTON_TEXT', 'Set password')
            ) : (
              t('FLEET_RESET_PASSWORD_BUTTON_TEXT', 'Change password')
            )}
          </Button>

          <Button
            color="primary"
            className="text-primary"
            fullWidth
            size="small"
            onClick={() => history.push('/auth/login')}
            style={{ textTransform: 'none' }}
          >
            {isConfigMode
              ? t('FLEET_CONFIG_BACK_TO_LOGIN_BUTTON_TEXT', 'Go to login')
              : t('FLEET_BACK_TO_LOGIN_BUTTON_TEXT', 'Back to login')}
          </Button>
        </ValidatorForm>
      </Box>
    </Box>
  );
};

export default connect(
  (state: ApplicationState) => state.app,
  AppActionCreators
)(ResetPassword as any);
